import React from 'react';

import { Field as FieldType } from './index.types';
import { getFieldComponent } from './Fields';
import { FieldValue, Errors } from './Fields/types';
import { Box } from '@mui/material';
import { StyledTitle } from './index.styles';
import { StyledFlex } from 'src/components/StyledFlex';

import { usePlatform } from 'src/redux-file/hooks';
import { useLocation } from 'react-router-dom';
import { AIAnswerImprover } from '../AIAnswerImprover';
import FieldAnswerComments from 'src/QuestionnairesV3/Fields/FieldAnswerComments';

type Props = {
  field: FieldType;
  fieldAnswerId?: number;
  errors: Errors;
  value?: FieldValue;
  onForceQuestionAnswerSave: () => void;
  onChange: (field: FieldType, value: FieldValue) => void;
  hints?: any[];
};

export default function Field(props: Props) {
  const {
    field,
    fieldAnswerId,
    errors,
    onChange,
    onForceQuestionAnswerSave,
    hints,
  } = props;
  const location = useLocation();

  const FieldComponent = React.useMemo(
    () => getFieldComponent(field.visual_type),
    [field.visual_type]
  );

  const { user } = usePlatform();

  const onChangeCallback = React.useCallback(
    (value: FieldValue) => {
      onChange(field, value);
    },
    [field, onChange]
  );

  const value = props.value ?? '';
  const { type, title } = field;
  const showTitle = title && type !== 'empty';

  const isNotReadonly = !location.pathname.includes('questionnaire-readonly');
  const isNotPreview = !location.pathname.includes('questionnaire-preview');
  const actionsAvailable = fieldAnswerId && user?.id && isNotReadonly;

  const nMaxIcons = 2;
  const iconSize = '40px';
  const magicMargin = '36px';
  const actionsButtonsMaxWidthPlusMargin = `calc(${nMaxIcons} * ${iconSize} + ${magicMargin})`;

  return (
    <>
      {showTitle && <StyledTitle>{field.title}</StyledTitle>}
      <Box
        style={{
          width: `calc(100% + ${actionsButtonsMaxWidthPlusMargin})`,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Box width={`calc(100% - ${actionsButtonsMaxWidthPlusMargin})`}>
          <FieldComponent
            field={field}
            fieldAnswerId={fieldAnswerId}
            value={value}
            onChange={onChangeCallback}
            errors={errors}
            onForceQuestionAnswerSave={onForceQuestionAnswerSave}
            hints={hints}
          />
        </Box>
        <Box width={actionsButtonsMaxWidthPlusMargin}>
          <Box
            sx={{
              ml: magicMargin,
              mt: field?.title ? '10px' : '30px',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {isNotReadonly && isNotPreview && (
              <FieldAnswerComments fieldAnswerId={fieldAnswerId} />
            )}
            {field.visual_type === 'simple-text-field' && (
              <AIAnswerImprover
                fieldAnswerId={fieldAnswerId}
                setAnswer={onChangeCallback}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
